import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  register: (data) => api.post('/api/auth/register', data),
  login: (data) => api.post('/api/auth/login', data),
  googleAuth: (access_token) => api.post('/api/auth/google', { access_token }),
  getMe: () => api.get('/api/users/me'),
  forgotPassword: (email) => api.post('/api/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/api/auth/reset-password', { token, password }),
  deleteAccount: () => api.delete('/api/users/me'),
};

export const userAPI = {
  updateProfile: (name) => api.patch('/api/users/me/profile', { name }),
  changePassword: (currentPassword, newPassword) => api.patch('/api/users/me/password', { currentPassword, newPassword }),
};

export const meetingAPI = {
  create: (data) => api.post('/api/meetings', data),
  list: () => api.get('/api/meetings'),
  launch: (id) => api.post(`/api/meetings/${id}/launch`),
  get: (id) => api.get(`/api/meetings/${id}`),
  join: (id) => api.post(`/api/meetings/${id}/join`),
  leave: (id) => api.post(`/api/meetings/${id}/leave`),
  end: (id) => api.post(`/api/meetings/${id}/end`),
  getParticipants: (id) => api.get(`/api/meetings/${id}/participants`),
  getParticipantsHistory: (id) => api.get(`/api/meetings/${id}/participants/history`),
  
  // Visibility control endpoints
  toggleGlobalVisibility: (id, showParticipantsToAll) => 
    api.patch(`/api/meetings/${id}/visibility`, { showParticipantsToAll }),
  toggleParticipantVisibility: (meetingId, participantId, canSeeParticipants) => 
    api.patch(`/api/meetings/${meetingId}/participants/${participantId}/visibility`, { canSeeParticipants }),
  toggleGuestVisibility: (meetingId, guestId, canSeeParticipants) => 
    api.patch(`/api/meetings/${meetingId}/guests/${guestId}/visibility`, { canSeeParticipants }),
  
  // Waiting room endpoints
  getWaitingRoom: (id) => api.get(`/api/meetings/${id}/waiting-room`),
  approveGuest: (meetingId, waitingId) => api.post(`/api/meetings/${meetingId}/waiting-room/${waitingId}/approve`),
  rejectGuest: (meetingId, waitingId) => api.post(`/api/meetings/${meetingId}/waiting-room/${waitingId}/reject`),
  kickParticipant: (meetingId, participantId) => api.post(`/api/meetings/${meetingId}/kick/${participantId}`),
  kickGuest: (meetingId, guestId) => api.post(`/api/meetings/${meetingId}/kick-guest/${guestId}`),
  deleteMeeting: (id) => api.delete(`/api/meetings/${id}`),
  getStatus: (id) => api.get(`/api/meetings/${id}/status`),
};

export const publicAPI = {
  getMeetingByToken: (token) => axios.get(`${API_URL}/api/public/meeting/${token}`),
  joinAsGuest: (token, guestName) => axios.post(`${API_URL}/api/public/meeting/${token}/join-guest`, { guestName }),
  requestJoin: (token, guestName) => axios.post(`${API_URL}/api/public/meeting/${token}/request-join`, { guestName }),
  checkWaitingStatus: (waitingRoomId) => axios.get(`${API_URL}/api/public/waiting-room/${waitingRoomId}/status`),
  getParticipants: (token, guestId) => axios.get(`${API_URL}/api/public/meeting/${token}/participants/${guestId}`),
  leaveAsGuest: (token, guestId) => axios.post(`${API_URL}/api/public/meeting/${token}/leave-guest`, { guestId }),
};

export const recordingAPI = {
  upload: (formData) => {
    return axios.post(`${API_URL}/api/recordings/upload`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
  },
  uploadTracks: (formData) => {
    return axios.post(`${API_URL}/api/recordings/upload-tracks`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      }
    });
  },
  list: (page = 1, limit = 10, signal) => api.get('/api/recordings', { params: { page, limit }, signal }),
  listByMeeting: (meetingId) => api.get(`/api/recordings/meeting/${meetingId}`),
  // Returns a short-lived stream token; use as ?token= query param on the stream URL
  getStreamToken: (id, signal) => api.get(`/api/recordings/${id}/stream-token`, { signal }),
  getStreamUrl: (id, token) =>
    token
      ? `${API_URL}/api/recordings/${id}/stream?token=${encodeURIComponent(token)}`
      : `${API_URL}/api/recordings/${id}/stream`,
  transcribe: (id) => api.post(`/api/recordings/${id}/transcribe`),
  delete: (id) => api.delete(`/api/recordings/${id}`),
  getTracks: (id) => api.get(`/api/recordings/${id}/tracks`),
  getTrackStreamUrl: (id, trackId, token) => `${API_URL}/api/recordings/${id}/tracks/${trackId}/stream?token=${encodeURIComponent(token)}`,
};

export const paymentAPI = {
  createOrder: (plan, billing, phone) => api.post('/api/payments/create-order', { plan, billing, phone }),
  getOrderStatus: (orderId) => api.get(`/api/payments/order-status/${orderId}`),
  getSubscription: () => api.get('/api/payments/subscription'),
  getHistory: () => api.get('/api/payments/history'),
  cancelSubscription: () => api.post('/api/payments/cancel'),
};

export const paypalAPI = {
  createPlanOrder: (plan, billing) => api.post('/api/paypal/create-plan-order', { plan, billing }),
  capturePlanOrder: (orderID) => api.post('/api/paypal/capture-plan-order', { orderID }),
  createConsultationOrder: (name, email, message) =>
    axios.post(`${API_URL}/api/paypal/create-consultation-order`, { name, email, message }),
  captureConsultationOrder: (orderID) =>
    axios.post(`${API_URL}/api/paypal/capture-consultation-order`, { orderID }),
};

export const blockchainAPI = {
  stamp: (recordingId) => api.post(`/api/blockchain/stamp/${recordingId}`),
  getCertificate: (recordingId) => api.get(`/api/blockchain/certificate/${recordingId}`),
  getStatuses: (ids) => api.get(`/api/blockchain/statuses?ids=${ids.join(',')}`),
  getPdfUrl: (recordingId) => `${API_URL}/api/blockchain/certificate/${recordingId}/pdf`,
  verify: (hash) => axios.get(`${API_URL}/api/blockchain/verify/${hash}`),
};

export const appVersionAPI = {
  getLatest: () => axios.get(`${API_URL}/api/app-version`),
};

export const feedbackAPI = {
  submit: (data) => api.post('/api/feedback', data),
};

export default api;